import { useRef, useState } from 'react';
import { Film, ImagePlus, Link2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/**
 * Thumbnail picker for YouTube / Instagram publish dialogs:
 * AI story-style art (Gemini → OpenAI fallback), video frame samples, file upload, or URL.
 */
export function PublishThumbnailPicker({
  platform = 'youtube',
  videoUrl = '',
  title = '',
  description = '',
  scriptContext = '',
  tone = '',
  visualStyle = '',
  thumbnailUrl = '',
  onThumbnailUrlChange,
  frameUrls = [],
  onFrameUrlsChange,
  showFrameSampler = true,
  showAiGenerateButton = true,
  aiUrls: controlledAiUrls,
  onAiUrlsChange,
  helpText,
  label = 'Cover / thumbnail',
  testIdPrefix = 'publish-thumb',
}) {
  const fileInputRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [framesBusy, setFramesBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [internalAiUrls, setInternalAiUrls] = useState([]);
  const aiUrls = controlledAiUrls !== undefined ? controlledAiUrls : internalAiUrls;
  const setAiUrls = onAiUrlsChange || setInternalAiUrls;

  const infoLine = (description || scriptContext || '').trim().split('\n')[0]?.slice(0, 200) || '';

  const selectUrl = (url) => {
    onThumbnailUrlChange?.(url);
  };

  const handleGenerateAi = async () => {
    const t = (title || '').trim();
    if (!t) {
      toast.error('Add a title first so AI can render text on the thumbnail');
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post('/social-publish/ai-thumbnail-options', {
        platform,
        title: t,
        info_line: infoLine,
        script_excerpt: (scriptContext || description || '').trim().slice(0, 2000),
        tone: tone?.trim() || null,
        visual_style: visualStyle?.trim() || null,
        count: 3,
      });
      const urls = Array.isArray(data.urls) ? data.urls.filter(Boolean) : [];
      if (!urls.length) {
        toast.error('No thumbnails were generated');
        return;
      }
      setAiUrls(urls);
      selectUrl(urls[0]);
      toast.success(
        data.provider === 'openai'
          ? 'Story thumbnails ready (OpenAI fallback)'
          : 'Story thumbnails ready (Gemini)',
      );
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : err.message || 'AI thumbnail generation failed');
      setAiUrls([]);
    } finally {
      setAiBusy(false);
    }
  };

  const handleSampleFrames = async () => {
    const vu = videoUrl.trim();
    if (!vu) {
      toast.error('Enter a video URL first to sample frames');
      return;
    }
    setFramesBusy(true);
    try {
      const { data } = await api.post('/social-publish/youtube/thumbnail-frame-options', {
        video_url: vu,
      });
      const urls = Array.isArray(data.urls) ? data.urls.filter(Boolean) : [];
      if (urls.length < 1) {
        toast.error('Could not sample frames from video');
        return;
      }
      onFrameUrlsChange?.(urls);
      if (urls[0]) selectUrl(urls[0]);
      toast.success('Pick a frame or generate AI art above');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : err.message || 'Frame sampling failed');
      onFrameUrlsChange?.([]);
    } finally {
      setFramesBusy(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/social-publish/thumbnail-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.url) {
        selectUrl(data.url);
        toast.success('Thumbnail uploaded');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : err.message || 'Upload failed');
    } finally {
      setUploadBusy(false);
    }
  };

  const allTiles = [...aiUrls, ...frameUrls.filter((u) => !aiUrls.includes(u))];

  return (
    <div className="space-y-2" data-tour="publish-thumbnail-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={`${testIdPrefix}-url`}>{label}</Label>
        <div className="flex flex-wrap gap-1.5">
          {showAiGenerateButton ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg gap-1 text-xs h-8"
              disabled={aiBusy}
              onClick={handleGenerateAi}
              data-testid={`${testIdPrefix}-ai-btn`}
            >
              {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TwinneeLogo className="w-3.5 h-3.5" />}
              AI story thumbnail
            </Button>
          ) : null}
          {showFrameSampler ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg gap-1 text-xs h-8"
              disabled={framesBusy || !videoUrl.trim()}
              onClick={handleSampleFrames}
              data-testid={`${testIdPrefix}-frames-btn`}
            >
              {framesBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
              Sample frames
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-lg gap-1 text-xs h-8"
            disabled={uploadBusy}
            onClick={() => fileInputRef.current?.click()}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {uploadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            Upload image
          </Button>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileUpload}
      />
      <p className="text-[11px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
        {helpText
          || (showAiGenerateButton
            ? `Generates cinematic ${platform === 'instagram' ? '9:16 Reels' : '16:9 YouTube'} cover art with your title and hook (Gemini, ChatGPT fallback). Or sample frames, upload a file, or paste an image URL.`
            : `Pick a generated thumbnail below, sample frames from your video URL, upload a file, or paste an image URL.`)}
      </p>
      {allTiles.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {allTiles.map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => selectUrl(u)}
              className="relative rounded-lg overflow-hidden border-2 transition-colors aspect-video bg-black/40 focus:outline-none focus-visible:ring-2"
              style={{
                borderColor: thumbnailUrl === u ? 'var(--primary)' : 'var(--glass-border)',
              }}
              title="Use this thumbnail"
              data-testid={`${testIdPrefix}-tile`}
            >
              <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
              {thumbnailUrl === u ? (
                <span
                  className="absolute bottom-1 right-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
                >
                  Selected
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2 items-center">
        <Link2 className="w-3.5 h-3.5 shrink-0 opacity-50" />
        <Input
          id={`${testIdPrefix}-url`}
          placeholder="https://… JPEG/PNG thumbnail URL"
          value={thumbnailUrl}
          onChange={(e) => onThumbnailUrlChange?.(e.target.value)}
          className="rounded-lg text-sm flex-1"
          data-testid={`${testIdPrefix}-url-input`}
        />
      </div>
    </div>
  );
}
